'use client';

import type { FC } from 'react';
import { Empty, Flex, Input, Space, Typography } from 'antd';
import { EvimoPagination } from '~/app/components/common/EvimoTable/EvimoPagination';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { KmsHitlReviewModal } from './components/KmsHitlReviewModal/KmsHitlReviewModal';
import { useKmsHitlQueue } from './hooks/useKmsHitlQueue';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Title, Text } = Typography;

export interface KmsHitlQueueProps {
  organizationId: string | null;
  isSuperAdmin?: boolean;
  isPlatformLibrary?: boolean;
}

export const KmsHitlQueue: FC<KmsHitlQueueProps> = ({
  organizationId,
  isSuperAdmin = false,
  isPlatformLibrary = false,
}) => {
  const { t } = useTranslation('kms');
  const queue = useKmsHitlQueue({
    organizationId,
    isSuperAdmin,
    isPlatformLibrary,
  });

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space direction="vertical" size={2}>
        <Title level={5} className="ds-section-header" style={{ margin: 0 }}>
          {t('hitl.title')}
        </Title>
        <Text type="secondary" className="om-dashboard-header__subtitle">
          {t('hitl.subtitle')}
        </Text>
      </Space>

      <Input.Search
        allowClear
        enterButton
        prefix={<SearchOutlined />}
        placeholder={t('hitl.searchDocuments')}
        value={queue.search}
        onChange={(event) => queue.onSearchChange(event.target.value)}
        onSearch={(value) => queue.onSearchChange(value)}
        style={{ maxWidth: 480 }}
      />

      {queue.total === 0 && !queue.isLoading ? (
        <Empty description={t('hitl.empty')} />
      ) : (
        <>
          <EvimoTable
            rowKey="documentId"
            columns={queue.columns}
            dataSource={queue.data}
            loading={queue.isLoading}
            pagination={false}
            scroll={{ x: 1000 }}
          />
          <Flex justify="flex-end" style={{ width: '100%' }}>
            <EvimoPagination
              current={queue.page}
              pageSize={queue.pageSize}
              total={queue.total}
              pageSizeOptions={queue.pageSizeOptions.map(String)}
              onChange={queue.onPageChange}
              onShowSizeChange={queue.onPageChange}
            />
          </Flex>
        </>
      )}

      <KmsHitlReviewModal
        item={queue.selectedItem}
        open={Boolean(queue.selectedItem)}
        onClose={queue.closeModal}
        candidates={queue.candidates}
        selectedCandidateKey={queue.selectedCandidateKey}
        onCandidateChange={queue.onCandidateChange}
        resolvedDocumentType={queue.resolvedDocumentType}
        onResolvedDocumentTypeChange={queue.setResolvedDocumentType}
        documentTypeOptions={queue.documentTypeOptions}
        overrideAssetId={queue.overrideAssetId}
        onOverrideAssetChange={queue.setOverrideAssetId}
        assetOptions={queue.assetOptions}
        assetsLoading={queue.assetsLoading}
        canResolve={queue.canResolve}
        canMarkGlobal={queue.canMarkGlobal}
        isPlatformLibrary={queue.isPlatformLibrary}
        isOrgWideReview={queue.isOrgWideReview}
        isAssetLinkReview={queue.isAssetLinkReview}
        onConfirm={queue.confirmSelected}
        onOverride={queue.overrideSelected}
        onMarkGlobal={queue.markGlobal}
        isResolving={queue.isResolving}
      />
    </Space>
  );
};
